/**
 * Cleaning list derivation: merge persisted cleaning verification data into
 * a flat per-clean row list suitable for the Costs page Cleaning tab.
 *
 * Source: cleaning_verification_results doc (one per entity+month).
 *
 * Rules:
 *   - Turnover cleans come from parsed_cleans[] (already exploded per clean,
 *     carries match data). Never from lines[] (that would double-count).
 *   - Non-turnover and unparseable come from lines[] WHERE classification
 *     is NOT "turnover_clean".
 *   - Bundle allocation: source_amount on an exploded clean is the FULL
 *     bundle total repeated on every piece. Per-clean amount = equal split
 *     with rounding remainder on the last piece so the N pieces sum exactly
 *     to source_amount.
 */

/**
 * Allocate a bundle total evenly across N pieces, distributing the rounding
 * remainder (in the smallest unit = 0.01) to the last piece.
 *
 * @param {string|number} total  The bundle total (string Decimal from backend)
 * @param {number} n             Number of pieces (bundle_size)
 * @returns {number[]}           Array of length n, summing exactly to total
 */
export function allocateBundle(total, n) {
  if (n <= 0) return [];
  if (n === 1) return [Number(total)];

  const totalNum = Number(total);
  // Round per-piece to 2 decimal places (floor), remainder on last piece.
  const perPiece = Math.floor((totalNum / n) * 100) / 100;
  const allocated = Array(n).fill(perPiece);
  // Remainder = total - sum of first (n-1) pieces, computed to avoid float drift.
  // Round to 2dp to avoid fp noise.
  allocated[n - 1] = Math.round((totalNum - perPiece * (n - 1)) * 100) / 100;
  return allocated;
}

/**
 * Merge parsed_cleans and lines from a single month's verification result
 * into a flat row list. Returns rows sorted by clean_date ascending.
 *
 * @param {object} monthResult  A cleaning_verification_results document
 * @returns {object[]}          Flat row list
 */
export function mergeMonthCleans(monthResult) {
  if (!monthResult) return [];
  const rows = [];
  const month = monthResult.month || '';
  const entity_id = monthResult.entity_id || '';
  const entity_name = monthResult.entity_name || entity_id;
  const region = monthResult.region || 'uk';

  // 1. Turnover cleans from parsed_cleans (with bundle allocation).
  // Group by (statement_id, source_description) to identify bundle siblings
  // so we can assign the correct allocated slice to each piece.
  const bundleGroups = {};
  for (const pc of (monthResult.parsed_cleans || [])) {
    if (pc.is_bundled) {
      const key = `${pc.statement_id}||${pc.source_description}`;
      if (!bundleGroups[key]) bundleGroups[key] = [];
      bundleGroups[key].push(pc);
    }
  }

  // Pre-compute allocations per bundle group.
  const bundleAllocations = new Map();
  for (const [key, group] of Object.entries(bundleGroups)) {
    const total = group[0].source_amount;
    const n = group[0].bundle_size || group.length;
    const amounts = allocateBundle(total, n);
    // Assign by index within the group (order matches parsed_cleans order).
    for (let i = 0; i < group.length; i++) {
      bundleAllocations.set(group[i], amounts[i] ?? amounts[amounts.length - 1]);
    }
  }

  for (const pc of (monthResult.parsed_cleans || [])) {
    const amount = pc.is_bundled
      ? (bundleAllocations.get(pc) ?? Number(pc.source_amount))
      : Number(pc.source_amount);

    rows.push({
      month,
      entity_id,
      entity_name,
      region,
      clean_date: pc.clean_date || '',
      property_id: pc.property_id || '',
      property_name: pc.property_name_raw || pc.property_id || '',
      classification: 'turnover_clean',
      match_outcome: pc.match_outcome || '',
      matched_checkouts: pc.matched_checkouts || [],
      amount,
      source_amount: Number(pc.source_amount),
      is_bundled: !!pc.is_bundled,
      bundle_size: pc.bundle_size || 1,
      agency: pc.agency || '',
      statement_id: pc.statement_id || '',
      non_turnover_reason: null,
    });
  }

  // 2. Non-turnover and unparseable from lines[].
  for (const line of (monthResult.lines || [])) {
    if (line.classification === 'turnover_clean') continue;

    rows.push({
      month,
      entity_id,
      entity_name,
      region,
      clean_date: (line.parsed_dates && line.parsed_dates[0]) || '',
      property_id: line.property_ref || '',
      property_name: line.property_name_raw || line.property_ref || '',
      classification: line.classification,
      match_outcome: null,
      matched_checkouts: [],
      amount: Number(line.amount) || 0,
      source_amount: Number(line.amount) || 0,
      is_bundled: false,
      bundle_size: 1,
      agency: line.agency || '',
      statement_id: line.statement_id || '',
      non_turnover_reason: line.non_turnover_reason || null,
    });
  }

  rows.sort((a, b) => a.clean_date.localeCompare(b.clean_date));
  return rows;
}

/**
 * Merge multiple months of cleaning verification results into a single
 * flat row list grouped by month.
 *
 * @param {object[]} monthResults  Array of cleaning_verification_results docs
 * @returns {object[]}             All rows, sorted by month then clean_date
 */
export function mergeAllMonths(monthResults) {
  const all = [];
  for (const mr of (monthResults || [])) {
    all.push(...mergeMonthCleans(mr));
  }
  all.sort((a, b) => a.month.localeCompare(b.month) || a.clean_date.localeCompare(b.clean_date));
  return all;
}

/**
 * Compute the correct month total from the original statement lines (not from
 * summing exploded per-clean amounts, which risks double-counting if there's
 * an allocation bug). This is the source-of-truth total.
 *
 * @param {object} monthResult  A cleaning_verification_results document
 * @returns {number}            Total cleaning cost for the month
 */
export function monthStatementTotal(monthResult) {
  if (!monthResult?.lines) return 0;
  let total = 0;
  for (const line of monthResult.lines) {
    total += Number(line.amount) || 0;
  }
  return Math.round(total * 100) / 100;
}
